import {
  FC,
  HTMLAttributes,
  useEffect,
  useState,
  useRef
} from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import { parseNumber } from "utils/number";
import { fadeIn } from "utils/animation";
import ReelImg from "assets/images/reel.png";
import SparkImg from "assets/images/sparks.png";
import { ApngPlayer } from "components/ApngPlayer";
import { animated, useSpring, easings } from "@react-spring/web";
import { Timer } from "components/Timer";
import { useTournamentDetails, useCurrency } from "hooks";
import { Center, GreenBox } from "utils/constants";
import { useScale } from "hooks/size";
import { useSoundStore } from "utils/sound-store";
import { Sounds } from "types/sound";
import { useDebounceCallback, useInterval } from "usehooks-ts";
import { useOrientation } from "hooks/orientation";
import { useSkinnedAssets } from "hooks/skin";
import { useStore } from "utils/store";

enum Status {
  Inactive,
  Active,
  Picked,
}

const Container = styled(Center)`
  position: absolute;
  width: 278px;
  height: 423px;
  left: calc((100% - 278px) / 2);
  top: calc((100% - 423px) / 2);
  margin: auto;
  border-image-source: url(images/ticket-container.svg);
  border-image-slice: 26 28 32 fill;
  border-image-width: auto;
  flex-direction: column;
`;

const Sparks = styled.div`
  position: absolute;
  width: 79px;
  height: 158px;
  left: 235px;
  top: 0px;
  pointer-events: none;

  &:first-child {
    left: -35px;
    top: 320px;
  }

  &:nth-child(2) {
    left: 231px;
    top: 210px;
  }

  &:nth-child(3) {
    left: -30px;
    top: 100px;
  }
`;

const Header = styled(GreenBox)`
  width: 248px;
  height: 56px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 0;
  box-shadow: none;
  background: transparent;

  img {
    width: 100%;
    height: 100%;
  }
`;

const Text = styled.p`
  font-family: DS-Digital;
  font-size: 1.16rem;
  font-weight: 700;
  text-align: center;
  color: #fff;
  top: 10px;
  position: absolute;
  width: 80%;
  left: 10%;
`;

const Reels = styled.div`
  width: 252px;
  height: 260px;
  display: grid;
  grid-template-columns: repeat(3, 84px);
  grid-template-rows: 100%;
`;

const Reel = styled.div<{ $status: Status; $picked: boolean }>`
  width: 100%;
  height: 100%;
  cursor: pointer;
  position: relative;
  pointer-events: ${(props) => props.$status === Status.Picked ? "none" : "auto"};

  &::after {
    content: "";
    position: absolute;
    width: calc(100% - 7px);
    height: calc(100% - 13px);
    left: 1px;
    top: 3px;
    border: 2px solid #fff;
    border-radius: 8px;
    box-shadow: 0 0 8px #fff;
    opacity: 0;
  }

  &::before {
    content: "";
    position: absolute;
    width: calc(100% - 7px);
    height: calc(100% - 13px);
    left: 3px;
    top: 6px;
    border-radius: 8px;
    background-color: #fff;
    z-index: 1;
    opacity: ${(props) => (props.$picked ? 1 : 0)};
    ${(props) => {
      if (props.$picked) {
        return css`
          animation: ${fadeIn()} 0.2s ease-out reverse forwards;
        `;
      }
    }}
  }

  ${(props) => {
    if (props.$status !== Status.Inactive) {
      return css`
        &:nth-child(even)::after {
          animation: ${fadeIn()} 0.3s linear alternate infinite;
        }

        &:nth-child(odd)::after {
          animation: ${fadeIn()} 0.3s linear alternate-reverse infinite;
        }
      `;
    }
  }}
`;

const Display = styled.div`
  width: 238px;
  height: 72px;
  padding: 4px 5px 0;
  background: url(images/ticket-display.webp) top center / contain no-repeat;
  display: grid;
  grid-template-rows: 1fr 18px 20vh;
  grid-template-columns: 70px 1fr 70px 1fr 70px;
  gap: 1px 0;
`;

const Label = styled.div`
  font-size: 0.68rem;
  grid-row: 1 / 2;
  color: #fff;
  text-align: center;
  line-height: 1;
`;

const Tournament = styled.div`
  position: relative;
  font-family: DS-Digital;
  color: #0f0;
  font-size: 0.83rem;
  font-weight: 700;
  line-height: 0.74;
  text-align: center;
  animation: ${fadeIn()} 0.15s linear 3 alternate-reverse;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  grid-column: 1 / 2;
  grid-row: 2 / 3;
`;

const DatePlayed = styled(Tournament)`
  grid-column: 3 / 4;
`;

const Bounty = styled(Tournament)`
  grid-column: 5 / 6;
`;

const Dispenser = styled(Tournament)`
  margin-top: 23px;
  grid-column: 2 / 5;
  grid-row: 3 / 4;
`;

const TicketBack = styled(animated.div)`
  left: calc((100% - 98px) / 2);
  position: absolute;
  width: 94px;
  height: 146px;
  background: url(images/card-back.png) center / contain no-repeat;
`;

export type TicketMachineProps = HTMLAttributes<HTMLDivElement> & {
  onFinish: () => void;
};

export const TicketMachine: FC<TicketMachineProps> = ({ onFinish, ...props }) => {
  const { t } = useTranslation();
  const [count, setCount] = useState(0);
  const [picked, setPicked] = useState(-1);
  const { tournamentSize, date, highestBounty } = useTournamentDetails();
  const { parseCurrency } = useCurrency();
  const { scale } = useScale();
  const play = useSoundStore(state => state.play);
  const stopRef = useRef(false);
  const { landscape } = useOrientation();
  const { header } = useSkinnedAssets();
  const playerId = useStore(state => state.playerId);

  const debPlay = useDebounceCallback((sound: Sounds, rate = 1) => {
    play(sound, rate);
  }, 1000)

  const handlePicked = (n: number) => {
    stopRef.current = true;
    play(Sounds.TicketClick);
    debPlay(Sounds.Dispense);
    setPicked(n);
  };

  const parseDate = (s: string) => {
    const d = new Date(s);
    return `${d.getMonth() + 1}-${d.getDate()}-${d.getFullYear()}`;
  };

  const getStatus = () => {
    return picked >= 0 ? Status.Picked : count > 0 ? Status.Active : Status.Inactive;
  };

  const [spring0, api0] = useSpring(() => ({
    transform: `translateY(-100vh) scale(${scale})`,
    opacity: 1,
  }));

  const [spring1, api1] = useSpring(() => ({
    transform: `translateY(-120%)`,
    config: {
      duration: 500,
      easing: easings.easeInSine,
    },
  }));

  useInterval(() => {
    play(Sounds.CardDrop);
  }, !stopRef.current ? 100 : null);
  
  useEffect(() => {
    api0.start({
      transform: `translateY(${landscape ? 0 : 2.5 * scale}vh) scale(${scale})`,
      opacity: picked < 0 ? 1 : 0,
      delay: picked < 0 ? 0 : 2000,
    })
  }, [api0, landscape, picked, scale]);

  useEffect(() => {
    api1.start({
      transform: picked < 0 ? `translateY(-120%)` : `translateY(120%)`,
      delay: 1000,
      onRest: () => {
        picked >= 0 && onFinish();
      }
    })
  }, [picked, api1, onFinish]);

  const handleTimer = () => {
    handlePicked(Math.floor(Math.random() * 3));
  };

  const handleReel = () => {
    stopRef.current = true;
    if(picked < 0) setCount(3);
  };

  const parseString = (s: string, maxLimit = 8) => {
    if(s.length > maxLimit) return '...'.concat(s.slice(-8));
    return s;
  };

  return (
    <Container style={spring0} data-testid="ticket-machine">
      <Sparks>
        <ApngPlayer delay={0.1} rate={3} src={SparkImg} />
      </Sparks>
      <Sparks>
        <ApngPlayer delay={0.1} rate={2.75} src={SparkImg} />
      </Sparks>
      <Sparks>
        <ApngPlayer delay={0.1} rate={2.5} src={SparkImg} />
      </Sparks>
      <Sparks>
        <ApngPlayer delay={0.1} rate={2.25} src={SparkImg} />
      </Sparks>
      <Header><img src={header} alt="" /></Header>
      <Reels>
        <Reel
          $status={getStatus()}
          $picked={picked === 0}
          onClick={() => handlePicked(0)}
          data-testid="ticket-machine-reel-0"
        >
          <ApngPlayer delay={0.25} rate={0.75} src={ReelImg} showAtOnce={true} onEnd={handleReel} />
          <Text>{parseNumber(tournamentSize)}</Text>
        </Reel>
        <Reel
          $status={getStatus()}
          $picked={picked === 1}
          onClick={() => handlePicked(1)}
          data-testid="ticket-machine-reel-1"
        >
          <ApngPlayer delay={0.25} rate={1} src={ReelImg} showAtOnce={true} />
          <Text>{parseNumber(tournamentSize)}</Text>
        </Reel>
        <Reel
          $status={getStatus()}
          $picked={picked === 2}
          onClick={() => handlePicked(2)}
          data-testid="ticket-machine-reel-2"
        >
          <ApngPlayer delay={0.25} rate={1.25} src={ReelImg} showAtOnce={true} />
          <Text>{parseNumber(tournamentSize)}</Text>
        </Reel>
      </Reels>
      <Display>
        <Label style={{ gridColumn: '1 / 2' }}>{t('PLAYER #')}</Label>
        <Label style={{ gridColumn: '3 / 4' }}>{t('DATE PLAYED')}</Label>
        <Label style={{ gridColumn: '5 / 6' }}>{t('BIGGEST BOUNTY')}</Label>
        { picked >= 0 && 
          <>
            <Tournament data-testid="ticket-machine-tournament-id">{parseString(playerId)}</Tournament>
            <DatePlayed>{date ? parseDate(date) : ''}</DatePlayed>
            <Bounty>{parseCurrency(highestBounty)}</Bounty>
          </>
        }
        <Dispenser>
          <TicketBack style={spring1} />
        </Dispenser>
      </Display>
      {!!count && picked < 0 && (
        <Timer seconds={count} onFinish={handleTimer} />
      )}
    </Container>
  );
};
